export function getDynamicText(text: string, variables: { [key: string]: string | number }): string {
  // Use a regular expression to find all placeholders between double curly braces
  const placeholderRegex = /\{\{([^}]+)\}\}/g

  // Replace each placeholder with the corresponding value from the variables object
  const dynamicText = text.replace(placeholderRegex, (match, placeholder) => {
    // Trim the placeholder to remove any leading or trailing spaces
    const keyName = placeholder.trim()

    // Check if the keyName exists in the variables object
    if (variables.hasOwnProperty(keyName)) {
      // If the keyName exists, return its value
      return variables[keyName].toString()
    } else {
      // If the keyName does not exist, return the original placeholder
      console.warn(`keyName: ${keyName} doesn't exist for the text: ${text}`)
      return match
    }
  })

  return dynamicText
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function stringToUppercase(string) {
  return string.toUpperCase()
}


type CommonHTMLTags = 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span' | 'a' | 'ul' | 'ol' | 'li' | 'table' | 'tr' | 'td' | 'th' | 'thead' | 'tbody' | 'footer' | 'header' | 'section' | 'article' | 'nav' | 'aside' | 'main' | 'figure' | 'figcaption' | 'img' | 'video' | 'audio' | 'iframe' | 'form' | 'input' | 'textarea' | 'button' | 'select' | 'option' | 'label' | 'fieldset' | 'legend' | 'strong' | 'em' | 'i' | 'b' | 'u' | 's' | 'strike' | 'del' | 'ins' | 'mark' | 'code' | 'pre' | 'blockquote' | 'cite' | 'q' | 'abbr' | 'address' | 'time' | 'progress' | 'meter' | 'details' | 'summary'

/**
 * Replaces all occurrences of a specific HTML tag in a string with another tag.
 *
 * @param input - The string containing HTML content.
 * @param targetTag - The HTML tag name to replace (restricted to common HTML tags).
 * @param replacementTag - The HTML tag name to use as a replacement (restricted to common HTML tags).
 * @returns A string with the target tags replaced by the replacement tag.
 */
export const replaceTag = (input: string, targetTag: CommonHTMLTags, replacementTag: CommonHTMLTags): string => {
  const openingTagRegex = new RegExp(`<${targetTag}(\\s*[^>]*)>`, 'g')
  const closingTagRegex = new RegExp(`</${targetTag}>`, 'g')

  const replaced = input
    .replace(openingTagRegex, `<${replacementTag}$1>`) // Replace opening tag
    .replace(closingTagRegex, `</${replacementTag}>`)  // Replace closing tag

  return replaced
}

export const languageByCode = {
  'ar': 'العربية',     // Arabic
  'bg': 'Български',  // Bulgarian
  'bn': 'বাংলা',       // Bengali
  'ca': 'Català',    // Catalan
  'cat': 'Català',    // Catalan
  'cs': 'Čeština',   // Czech
  'de': 'Deutsch',   // German
  'el': 'Ελληνικά',  // Greek
  'en': 'English',   // English
  'es': 'Español',   // Spanish
  'fr': 'Français',  // French
  'hi': 'हिन्दी',       // Hindi
  'hu': 'Magyar',    // Hungarian
  'it': 'Italiano',  // Italian
  'ja': '日本語',     // Japanese
  'jv': 'Basa Jawa', // Javanese
  'mr': 'मराठी',      // Marathi
  'nl': 'Nederlands',// Dutch
  'pa': 'ਪੰਜਾਬੀ',       // Punjabi
  'pl': 'Polski',     // Polish
  'pt': 'Português', // Portuguese
  'ro': 'Română',     // Romanian
  'ru': 'Русский',    // Russian
  'sr': 'Српски',     // Serbian
  'sv': 'Svenska',    // Swedish
  'te': 'తెలుగు',      // Telugu
  'uk': 'Українська', // Ukrainian
  'zh': '中文',         // Mandarin Chinese
} as const


export const getInitials = (str: string): string => {
  if (!str?.trim()) return ''

  const words = str.trim().split(/\s+/) // Split by spaces, removing extra spaces
  const firstInitial = words[0][0]?.toUpperCase() ?? ''
  const lastInitial = words.length > 1 ? words[words.length - 1][0]?.toUpperCase() ?? '' : ''

  return lastInitial ? `${firstInitial}${lastInitial}` : firstInitial
}


export const getSlugAsString = (slug) => typeof slug === 'string' ? slug : slug.join('/')
